import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import PhotoUpload from "@/components/PhotoUpload";
import AnimalTypeSelector from "@/components/AnimalTypeSelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function NewSanctuaryResident() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [photos, setPhotos] = useState([]);
  const [animalName, setAnimalName] = useState("");
  const [animalType, setAnimalType] = useState("Dog");
  const [speciesOther, setSpeciesOther] = useState("");
  const [caseHistory, setCaseHistory] = useState("");
  const [arrivalDate, setArrivalDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!animalName.trim()) {
      toast({ variant: "destructive", title: "Animal name is required" });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/cases/sanctuary-residents", {
        animal_name: animalName.trim(),
        animal_type: animalType,
        species_other: animalType === "Other" ? speciesOther : null,
        case_history: caseHistory,
        arrival_date: arrivalDate,
        photos,
      });
      toast({ title: "Sanctuary resident saved!", description: `${data.animal_name} has been added.` });
      navigate(`/cases/${data.id}?created=1`);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-lg mx-auto px-4 py-6 pb-24">
        <button onClick={() => navigate(-1)} data-testid="new-sanctuary-resident-back-button" className="flex items-center gap-1 text-sm text-amtm-textMuted mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="font-display text-3xl text-amtm-text mb-6">New Sanctuary Resident</h1>

        <form onSubmit={handleSave} className="space-y-6" data-testid="new-sanctuary-resident-form">
          <div>
            <Label className="text-base mb-2 block">Photo</Label>
            <PhotoUpload photos={photos} setPhotos={setPhotos} testId="new-sanctuary-resident-photo-upload" />
          </div>

          <div>
            <Label htmlFor="sanctuary-animal-name" className="text-base">Animal Name</Label>
            <Input
              id="sanctuary-animal-name"
              value={animalName}
              onChange={(e) => setAnimalName(e.target.value)}
              placeholder="e.g. Bruno"
              required
              data-testid="new-sanctuary-resident-name-input"
              className="mt-1.5 h-14 text-lg"
            />
          </div>

          <div>
            <Label className="text-base mb-2 block">Animal Type</Label>
            <AnimalTypeSelector
              value={animalType}
              onChange={setAnimalType}
              speciesOther={speciesOther}
              onSpeciesOtherChange={setSpeciesOther}
              testIdPrefix="new-sanctuary-resident-animal-type"
            />
          </div>

          <div>
            <Label htmlFor="sanctuary-description" className="text-base">Short Description</Label>
            <Textarea
              id="sanctuary-description"
              value={caseHistory}
              onChange={(e) => setCaseHistory(e.target.value)}
              placeholder="e.g. Blind senior dog, loves belly rubs"
              data-testid="new-sanctuary-resident-description-input"
              className="mt-1.5 min-h-[80px] text-base"
            />
          </div>

          <div>
            <Label htmlFor="sanctuary-arrival-date" className="text-base">Arrival at Sanctuary Date</Label>
            <Input
              id="sanctuary-arrival-date"
              type="date"
              value={arrivalDate}
              onChange={(e) => setArrivalDate(e.target.value)}
              data-testid="new-sanctuary-resident-arrival-date-input"
              className="mt-1.5 h-12"
            />
          </div>

          <Button type="submit" disabled={saving} className="w-full h-14 text-lg font-semibold bg-amtm-teal text-white hover:bg-amtm-teal/90 rounded-xl shadow-md" data-testid="save-sanctuary-resident-button">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save Sanctuary Resident"}
          </Button>
        </form>
      </div>
    </div>
  );
}
